

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ArcEdge, ArcEvent, Event } from '@/lib/types';

import { SubscriptionLimitGate } from '@/components/subscription/subscription-limit-gate';
import { useNovel } from '@/context/novel-context';
import { useFirebase } from '@/firebase';
import { useToast } from '@/hooks/use-toast';
import { getNextRelationshipGraphPosition } from '@/lib/relationship-graph-layout';

interface CreateEventModalProps {
  open: boolean;
  onClose: () => void;
  novelId: string;
  arcId: string;
  characterId?: string;
  characterName?: string; // To help with silent population
  onSuccess: (newEvent: ArcEvent, newEdge?: ArcEdge) => void;
  initialPosition?: { x: number; y: number };
  sourceEventId?: string | null;
}

function generateId(): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let autoId = '';
  for (let i = 0; i < 20; i++) {
    autoId += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return autoId;
}

export function CreateEventModal({
  open,
  onClose,
  arcId,
  characterId,
  characterName,
  onSuccess,
  initialPosition,
  sourceEventId,
}: CreateEventModalProps) {
  const { backend } = useFirebase();
  const { novel } = useNovel();
  const { toast } = useToast();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const { t } = useTranslation();

  // Reset when opening
  useEffect(() => {
    if (open) {
      setName('');
      setDescription('');
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!novel.id || !arcId) return;

    // Final data with silent population if empty
    const finalName = name.trim() || (characterName ? `${characterName} - New Event` : t('arcs.untitled_event'));
    const finalDescription = description.trim() || (characterName ? t('arcs.automated_plot_point', { characterName }) : '');
    setIsSaving(true);
    try {
      const eventId = generateId();
      const graphPosition = getNextRelationshipGraphPosition(novel);

      const eventData: Event = {
        id: eventId,
        name: finalName,
        type: '',
        date: '',
        shortDescription: finalDescription,
        location: '',
        whyItMatters: '',
        involvedFactions: '',
        isNarrativelyComplete: false,
        characterIds: characterId ? [characterId] : [],
        organizationIds: [],
        locationIds: [],
        order: Date.now(),
        graphPosition,
        audit: {
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        },
      };

      // Create Event document and details
      await backend.entities.createEventWithDetails(novel.ownerId, novel, eventData, finalDescription);

      // Prepare Arc Event
      const newArcEvent: ArcEvent = {
        eventId,
        name: eventData.name,
        summary: eventData.shortDescription,
        position: initialPosition || { x: 100, y: 100 },
      };

      let newArcEdge: ArcEdge | undefined;

      // Create Edge if source is provided
      if (sourceEventId) {
        newArcEdge = {
          id: `edge-${sourceEventId}-${eventId}`,
          sourceEventId,
          targetEventId: eventId,
        };
      }

      onSuccess(newArcEvent, newArcEdge);

      toast({ title: t('arcs.success'), description: t('arcs.event_created_added') });
      handleClose();
    } catch (error) {
      console.error('Error creating event:', error);
      toast({ title: t('arcs.error'), description: t('arcs.event_add_failed'), variant: 'destructive' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    setName('');
    setDescription('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ fontWeight: 700 }}>{t('arcs.add_new_event_to_arc')}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
          <TextField
            label={t('arcs.event_name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            required
            autoFocus
          />

          <TextField
            label={t('arcs.short_summary')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
            helperText={t('arcs.summary_helper_text')}
          />

          {sourceEventId && (
            <Typography variant="caption" color="primary" sx={{ fontWeight: 600 }}>
              {t('arcs.will_be_connected')}
            </Typography>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isSaving}>
          {t('arcs.cancel')}
        </Button>
        <SubscriptionLimitGate limit="eventsPerNovel" currentCount={Object.keys(novel?.summaries.events ?? {}).length}>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isSaving || !name.trim()}
          >
            {isSaving ? t('arcs.saving') : t('arcs.create_and_add')}
          </Button>
        </SubscriptionLimitGate>
      </DialogActions>
    </Dialog>
  );
}
